import { z } from 'zod';
import type { RecordStatus } from '../enums';
import { digits, listQuerySchema, objectId, optionalText, recordStatus } from './common';
import type { UserRef } from './users';

/**
 * Tickets (reference PC722; a customer's tickets PC87). Screens, fields and option lists:
 * docs/product-inventory.md, appendix 11.4.
 */

/** In the order of the reference's status select. */
export const TICKET_STATUSES = [
  'Call Back',
  'Closed',
  'Escalation',
  'In Progress',
  'In Working',
  'Open',
  'Pending',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

/** The view buttons above the list, in the reference's order. */
export const TICKET_VIEWS = [
  ['all', 'All Tickets'],
  ['assigned', 'Assigned to Me'],
  ['created', 'Created By Me'],
  ['opened', 'Opened'],
  ['closed', 'Closed'],
  ['groups', 'Groups'],
] as const;
export type TicketView = (typeof TICKET_VIEWS)[number][0];
const VIEW_KEYS = TICKET_VIEWS.map(([key]) => key) as [TicketView, ...TicketView[]];

export const TICKET_ASSIGN_KINDS = ['user', 'group'] as const;
export type TicketAssignKind = (typeof TICKET_ASSIGN_KINDS)[number];

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
const id24 = (message: string) => z.string().regex(/^[a-f\d]{24}$/i, message);

/** "Add Ticket" and "Edit Ticket". */
export const ticketInputSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  typeId: id24('Select Ticket Type'),
  description: z.string().trim().min(1, 'Description is required').max(2000),
  /** The customer's number as shown on the profile ("Customer ID-100123"). */
  customerId: z
    .string()
    .trim()
    .regex(/^\d{1,11}$/, 'Customer ID is required'),
  customerName: z.string().trim().min(1, 'Customer Name is required').max(50),
  contactNumber: z.preprocess(blankToUndefined, digits(10, 10, 'Contact Number').optional()),
  alternateNumber: z.preprocess(blankToUndefined, digits(10, 10, 'Alternate Number').optional()),
  status: z.enum(TICKET_STATUSES, { required_error: 'Select Ticket Status' }),
  /** "Assign Ticket To": a user or a group. */
  assignTo: z.enum(TICKET_ASSIGN_KINDS).default('user'),
  assigneeId: id24('Select from the User List'),
});
export type TicketInput = z.input<typeof ticketInputSchema>;

/** "Post comment": a comment can also reassign the ticket and change its status and type. */
export const ticketCommentSchema = z.object({
  comment: z.string().trim().min(1, 'Comment is required').max(2000),
  assignTo: z.enum(TICKET_ASSIGN_KINDS),
  assigneeId: id24('Select Assign To'),
  status: z.enum(TICKET_STATUSES, { required_error: 'Select Ticket Status' }),
  typeId: id24('Select Ticket Type'),
});
export type TicketCommentInput = z.input<typeof ticketCommentSchema>;

export const ticketListQuerySchema = listQuerySchema.extend({
  view: z.enum(VIEW_KEYS).default('all'),
  ticketNo: optionalText(14),
  customerId: optionalText(11),
  status: z.enum(TICKET_STATUSES).optional(),
  typeId: objectId.optional(),
  /** "Sort By": Latest or Oldest. */
  order: z.enum(['latest', 'oldest']).default('latest'),
});

/** A customer's tickets (PC87): Date From, Date To, Ticket Number. */
export const customerTicketsQuerySchema = listQuerySchema.extend({
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  ticketNo: optionalText(14),
});

export interface TicketListItemDto {
  id: string;
  /** Who last changed the ticket and when ("Update By: ... on ..."). */
  updatedBy: UserRef | null;
  updatedAt: string;
  /** "TKT" followed by the running number. */
  ticketNo: string;
  title: string;
  description: string;
  type: string;
  /** Login ID of the user, or the name of the group, the ticket is with. */
  assignedTo: string;
  status: TicketStatus;
  customer: { id: string | null; customerId: number; name: string };
  createdBy: UserRef | null;
  createdAt: string;
}

export interface TicketCommentDto {
  id: string;
  text: string;
  /** What the comment changed, in words: "Status: Open to In Progress". */
  changes: string[];
  by: UserRef | null;
  at: string;
}

export interface TicketDto extends TicketListItemDto {
  typeId: string;
  contactNumber: string;
  alternateNumber: string;
  assignTo: TicketAssignKind;
  assigneeId: string;
  comments: TicketCommentDto[];
  lastCommentedAt: string | null;
}

/**
 * The eight tiles above the list, in the reference's order: TOTAL, CALLBACK, ESCALATION,
 * OPENED, CLOSED, IN PROGRESS, PENDING and MY TICKETS.
 */
export const TICKET_TILES = [
  ['total', 'TOTAL TICKETS'],
  ['callBack', 'CALLBACK TICKETS'],
  ['escalation', 'ESCALATION TICKETS'],
  ['opened', 'OPENED TICKETS'],
  ['closed', 'CLOSED TICKETS'],
  ['inProgress', 'IN PROGRESS TICKETS'],
  ['pending', 'PENDING TICKETS'],
  ['mine', 'MY TICKETS'],
] as const;
export type TicketTile = (typeof TICKET_TILES)[number][0];
export type TicketCounts = Record<TicketTile, number>;

/** Users and groups a ticket can be given to. */
export interface TicketAssignees {
  users: Array<{ id: string; loginId: string; name: string }>;
  groups: Array<{ id: string; name: string }>;
}

/* ------------------------------ Ticket types ------------------------------ */

export const ticketTypeInputSchema = z.object({
  name: z.string().trim().min(1, 'Ticket Type is required').max(100),
  status: recordStatus.default('Active'),
});
export type TicketTypeInput = z.input<typeof ticketTypeInputSchema>;
export const ticketTypeUpdateSchema = ticketTypeInputSchema.partial();

export interface TicketTypeDto {
  id: string;
  name: string;
  status: RecordStatus;
  createdBy: UserRef | null;
  createdAt: string;
  updatedBy: UserRef | null;
  updatedAt: string;
}

/* ------------------------------ Ticket groups ----------------------------- */

export const ticketGroupInputSchema = z.object({
  name: z.string().trim().min(1, 'Group Name is required').max(255),
  memberIds: z.array(objectId).min(1, 'Select at least one user').max(500),
});
export type TicketGroupInput = z.input<typeof ticketGroupInputSchema>;

export interface TicketGroupDto {
  id: string;
  name: string;
  members: UserRef[];
}
